import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Avatar } from '../../utils/avatar';
import { getPublicAssetPath } from '../../config';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    logout();
  };

  // Check if user has any admin scope (prefix admin.*)
  const hasAdminScope = () => {
    if (!user?.scopes || user.scopes.length === 0) {
      return false;
    }
    return user.scopes.some(scope => scope.startsWith('admin.'));
  };

  // Check if user has any templates scope (prefix templates.*)
  const hasTemplatesScope = () => {
    if (!user?.scopes || user.scopes.length === 0) {
      return false;
    }
    return user.scopes.some(scope => scope.startsWith('templates.'));
  };

  return (
    <header className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border-b fixed top-0 left-0 right-0 z-50 transition-colors duration-300`}>
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center space-x-8">
          <div className="flex items-center">
            <img
              src={getPublicAssetPath('/le_long.png')}
              alt="Legitimu Logo"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null; // Prevent infinite loop
                target.src = 'http://localhost:3000/legitimu/le_long.png';
              }}
              className="h-8 transition-all duration-300"
              style={darkMode ? { filter: 'brightness(0) invert(1)' } : { filter: 'none' }}
            />
          </div>
          <nav className="flex items-center space-x-6">
            <Link to="/dashboard" className={`text-sm font-medium ${isActive('/dashboard') ? 'text-violet-600 border-b-2 border-violet-600 pb-3' : darkMode ? 'text-gray-300 hover:text-violet-400' : 'text-gray-700 hover:text-violet-600'}`}>Dashboard</Link>
            <Link to="/summary" id="nav-summary" className={`text-sm font-medium ${isActive('/summary') ? 'text-violet-600 border-b-2 border-violet-600 pb-3' : darkMode ? 'text-gray-300 hover:text-violet-400' : 'text-gray-700 hover:text-violet-600'}`}>Summary</Link>
            <Link to="/board" className={`text-sm font-medium ${isActive('/board') ? 'text-violet-600 border-b-2 border-violet-600 pb-3' : darkMode ? 'text-gray-300 hover:text-violet-400' : 'text-gray-700 hover:text-violet-600'}`}>Agreements</Link>
            {hasTemplatesScope() && (
              <Link to="/templates" className={`text-sm font-medium ${isActive('/templates') ? 'text-violet-600 border-b-2 border-violet-600 pb-3' : darkMode ? 'text-gray-300 hover:text-violet-400' : 'text-gray-700 hover:text-violet-600'}`}>Templates</Link>
            )}
            {hasAdminScope() && (
              <Link to="/admin" className={`text-sm font-medium ${isActive('/admin') ? 'text-violet-600 border-b-2 border-violet-600 pb-3' : darkMode ? 'text-gray-300 hover:text-violet-400' : 'text-gray-700 hover:text-violet-600'}`}>Manage</Link>
            )}
          </nav>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg transition-colors ${darkMode
              ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <i className="fa-solid fa-sun text-lg"></i>
            ) : (
              <i className="fa-solid fa-moon text-lg"></i>
            )}
          </button>
          <button className={`relative p-2 ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'} rounded-lg transition-colors`}>
            <i className="fa-regular fa-bell text-lg"></i>
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          <div className={`relative pl-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} border-l`} ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center space-x-3 ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} rounded-lg px-2 py-1 transition-colors`}
            >
              <Avatar
                src={user?.avatar_url}
                name={user?.name}
                id={user?.id}
                className="w-8 h-8 rounded-full"
                alt="User"
              />
              <div className="flex items-center space-x-2">
                <div className="flex flex-col items-start">
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{user?.name || 'User'}</span>
                  {user?.role && (
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} capitalize`}>{user.role.replace('_', ' ')}</span>
                  )}
                </div>
                <i className={`fa-solid fa-chevron-down text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className={`absolute right-0 mt-2 w-56 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border shadow-lg z-50`}>
                <div className="py-1">
                  <Link
                    to="/profile"
                    onClick={() => setIsDropdownOpen(false)}
                    className={`flex items-center px-4 py-2 text-sm ${darkMode ? 'text-gray-200 hover:bg-gray-700 hover:text-violet-400' : 'text-gray-700 hover:bg-violet-50 hover:text-violet-700'} transition-colors`}
                  >
                    <i className={`fa-solid fa-user mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
                    My Profile
                  </Link>
                  <Link
                    to="/support"
                    onClick={() => setIsDropdownOpen(false)}
                    className={`flex items-center px-4 py-2 text-sm ${darkMode ? 'text-gray-200 hover:bg-gray-700 hover:text-violet-400' : 'text-gray-700 hover:bg-violet-50 hover:text-violet-700'} transition-colors`}
                  >
                    <i className={`fa-solid fa-life-ring mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
                    Support
                  </Link>
                  <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} my-1`}></div>
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center px-4 py-2 text-sm text-red-600 ${darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'} transition-colors`}
                  >
                    <i className="fa-solid fa-sign-out-alt mr-3"></i>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
